import { useState, useRef, useEffect, useCallback, useMemo } from 'react';

const useDragAndDrop = (isOpen, type) => {
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isVisible, setIsVisible] = useState(false);

    const modalRef = useRef(null);
    const dragStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });
    const isInitializedRef = useRef(false);
    const animationFrameRef = useRef(null);
    const lastUpdateTimeRef = useRef(0);

    // 모달이 열릴 때마다 위치 초기화
    useEffect(() => {
        if (isOpen && !isInitializedRef.current) {
            // 모달을 숨김 상태로 시작
            setIsVisible(false);

            const resizeObserver = new ResizeObserver((entries) => {
                for (let entry of entries) {
                    const modalWidth = entry.contentRect.width;
                    const modalHeight = entry.contentRect.height;

                    // 모달이 실제 크기를 가지게 되었을 때만 위치 계산
                    if (modalWidth > 0 && modalHeight > 0) {
                        const initialX = Math.max(0, window.innerWidth / 2 - modalWidth / 2);
                        const initialY = Math.max(0, window.innerHeight / 2 - modalHeight / 2);

                        setPosition({ x: initialX, y: initialY - (type === 'calculator' ? 170 : 0) });
                        isInitializedRef.current = true;

                        // 위치 계산 완료 후 모달을 보이게 함
                        setIsVisible(true);
                        resizeObserver.disconnect();
                    }
                }
            });

            if (modalRef.current) {
                resizeObserver.observe(modalRef.current);
            }

            return () => resizeObserver.disconnect();
        }

        // 모달이 닫힐 때 초기화 플래그 리셋
        if (!isOpen) {
            isInitializedRef.current = false;
            setIsVisible(false);
        }
    }, [isOpen]);

    // 입력 요소 체크
    const isInteractiveElement = useCallback((target) => {
        const interactiveTags = ['INPUT', 'SELECT', 'TEXTAREA', 'BUTTON'];
        return (
            interactiveTags.includes(target.tagName) ||
            target.closest('input') ||
            target.closest('select') ||
            target.closest('textarea') ||
            target.closest('button')
        );
    }, []);

    // 드래그 시작 핸들러
    const handleDragStart = useCallback(
        (e) => {
            // 입력 요소나 버튼에서 드래그 시작을 방지
            if (isInteractiveElement(e.target)) {
                return;
            }

            e.preventDefault();

            if (!modalRef.current) return;

            const rect = modalRef.current.getBoundingClientRect();
            const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
            const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? 0;

            dragStartRef.current = {
                x: clientX,
                y: clientY,
                offsetX: clientX - rect.left,
                offsetY: clientY - rect.top,
            };

            setIsDragging(true);
        },
        [isInteractiveElement]
    );

    // 드래그 중 핸들러
    const handleDragMove = useCallback(
        (e) => {
            if (!isDragging) return;

            e.preventDefault();

            const now = Date.now();
            const throttleDelay = 16;

            // Throttling 적용
            if (now - lastUpdateTimeRef.current < throttleDelay) {
                return;
            }

            // 이전 animationFrame이 있다면 취소
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }

            animationFrameRef.current = requestAnimationFrame(() => {
                const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
                const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? 0;

                const newX = clientX - dragStartRef.current.offsetX;
                const newY = clientY - dragStartRef.current.offsetY;

                // 화면 경계 체크
                const maxX = window.innerWidth - (modalRef.current?.offsetWidth ?? 0);
                const maxY = window.innerHeight - (modalRef.current?.offsetHeight ?? 0);

                setPosition({
                    x: Math.max(0, Math.min(newX, maxX)),
                    y: Math.max(0, Math.min(newY, maxY)),
                });

                lastUpdateTimeRef.current = now;
                animationFrameRef.current = null;
            });
        },
        [isDragging]
    );

    // 드래그 종료 핸들러
    const handleDragEnd = useCallback(() => {
        setIsDragging(false);

        // 진행 중인 animationFrame 정리
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
    }, []);

    // 모달이 브라우저 경계를 벗어났는지 체크하는 함수
    const checkModalBoundary = useCallback(() => {
        if (!modalRef.current) return false;

        const modalRect = modalRef.current.getBoundingClientRect();
        const modalWidth = modalRef.current.offsetWidth;
        const modalHeight = modalRef.current.offsetHeight;

        // 화면 경계 체크
        const maxX = window.innerWidth - modalWidth;
        const maxY = window.innerHeight - modalHeight;

        const isOutOfBounds = modalRect.left < 0 || modalRect.top < 0 || modalRect.left > maxX || modalRect.top > maxY;

        return isOutOfBounds;
    }, []);

    // 모달을 화면 안으로 이동시키는 함수
    const adjustModalPosition = useCallback(() => {
        if (!modalRef.current) return;

        const modalWidth = modalRef.current.offsetWidth;
        const modalHeight = modalRef.current.offsetHeight;

        const maxX = window.innerWidth - modalWidth;
        const maxY = window.innerHeight - modalHeight;

        const currentLeft = parseInt(modalRef.current.style.left) || 0;
        const currentTop = parseInt(modalRef.current.style.top) || 0;

        const adjustedX = Math.max(0, Math.min(currentLeft, maxX));
        const adjustedY = Math.max(0, Math.min(currentTop, maxY));

        modalRef.current.style.left = `${adjustedX}px`;
        modalRef.current.style.top = `${adjustedY}px`;

        // position 상태도 업데이트
        setPosition({ x: adjustedX, y: adjustedY });
    }, []);

    // 이벤트 리스너 관리
    const eventHandlers = useMemo(
        () => ({
            handleMouseMove: (e) => handleDragMove(e),
            handleMouseUp: () => handleDragEnd(),
            handleTouchMove: (e) => handleDragMove(e),
            handleTouchEnd: () => handleDragEnd(),
        }),
        [handleDragMove, handleDragEnd]
    );

    useEffect(() => {
        if (!isDragging) return;

        const { handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd } = eventHandlers;

        // 마우스 이벤트
        document.addEventListener('mousemove', handleMouseMove, { passive: false });
        document.addEventListener('mouseup', handleMouseUp);

        // 터치 이벤트
        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        document.addEventListener('touchend', handleTouchEnd);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };
    }, [isDragging, eventHandlers]);

    // 반환값 메모이제이션
    return useMemo(
        () => ({
            modalRef,
            position,
            isDragging,
            isVisible,
            handleDragStart,
            checkModalBoundary,
            adjustModalPosition,
        }),
        [position, isDragging, isVisible, handleDragStart, checkModalBoundary, adjustModalPosition]
    );
};

export default useDragAndDrop;
